import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { getStaffHasCateItemList } from "@/api/staff";
import type { StaffHasCateItem } from "@/api/staff";

import { StaffCateItem } from "./StaffCateItem";

const BRAND = "#FF6B35";

function useListPage<T>(
  fetchFn: (query: { page: number; limit: number }) => Promise<T[]>,
  limit = 10,
) {
  const [data, setData] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState<"more" | "no-more" | "loading">("more");

  const init = async (reset = false) => {
    if (loading) return;
    const nextPage = reset ? 1 : page;
    setLoading(true);
    setLoadingStatus("loading");
    try {
      const list = (await fetchFn({ page: nextPage, limit })) ?? [];
      setData((prev) => (reset ? list : [...prev, ...list]));
      setLoadingStatus(list.length < limit ? "no-more" : "more");
      if (reset) setPage(1);
    } catch (e) {
      console.error("[MyCate] load list failed:", e);
      setLoadingStatus("no-more");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (page > 1) init(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return { data, loading, loadingStatus, init, setPage, setData, setLoadingStatus };
}

export default function MyCatePage() {
  const navigate = useNavigate();
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const fetchMyCate = async ({ page, limit }: { page: number; limit: number }) => {
    const res = await getStaffHasCateItemList({ page, limit });
    return res?.list || [];
  };

  const { data, loading, loadingStatus, init, setPage, setData, setLoadingStatus } =
    useListPage<StaffHasCateItem>(fetchMyCate, 100);

  useEffect(() => {
    // 已申请项目：按接口约定一次拉取 100 条
    init(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (!e?.isIntersecting) return;
        if (loading) return;
        if (loadingStatus !== "more") return;
        setPage((p) => p + 1);
      },
      { root: null, rootMargin: "200px 0px 200px 0px", threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loading, loadingStatus, setPage]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header — 品牌橙色 */}
      <div style={{ background: BRAND }}>
        <div className="px-5 flex items-center gap-3" style={{ paddingTop: "calc(env(safe-area-inset-top,0px) + 16px)", paddingBottom: 13 }}>
          <button onClick={() => navigate(-1)} className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer active:scale-[0.98] shrink-0" style={{ background: "rgba(255,255,255,0.2)" }}>
            <i className="ri-arrow-left-line" style={{ fontSize: 20, color: "#fff" }} />
          </button>
          <span className="text-lg font-bold text-white">我的项目</span>
        </div>
      </div>

      {/* 列表 */}
      <div className="bg-white min-h-[calc(100vh-120px)]">
        <div className="px-4 pt-4 space-y-0">
          {data.map((item) => (
            <StaffCateItem key={item.id} item={item} />
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div className="pt-16 pb-8 text-center">
            <i className="ri-loader-4-line text-2xl animate-spin text-gray-400 block mx-auto mb-2" />
            <p className="text-xs text-gray-400">加载中...</p>
          </div>
        )}

        {!loading && loadingStatus === "no-more" && data.length > 0 && (
          <div className="py-6 text-center text-gray-300 text-xs">— 已经到底了 —</div>
        )}

        {/* Empty state */}
        {!loading && !data.length && (
          <div className="pt-16 pb-8 text-center">
            <i className="ri-apps-line text-5xl block mx-auto mb-3 text-gray-200" />
            <p className="text-sm text-gray-400">暂无已申请的项目</p>
          </div>
        )}

        {/* 触底加载哨兵 */}
        <div ref={loadMoreRef} style={{ height: 1 }} />
      </div>
    </div>
  );
}
